// algolia-conversion-events.js
import { hasAnalyticsConsent } from "@services/algolia-insights"
import aa from "search-insights"


/**
 * Attributing a basket add back to the search that caused it.
 *
 * Dynamic Re-Ranking only reorders a query's results once it has evidence
 * that people pick a particular record for that query. On our plan its only
 * available Goal is "Conversion rate" - the dashboard offers no
 * click-through-rate goal - so clicks alone produce nothing. The preview run
 * on 2026-09-16 confirmed it: "0 queries re-ranked".
 *
 * A conversion only counts if it carries the `queryID` Algolia returned with
 * the search. That queryID is handed to the results list, but the shopper
 * then navigates to a product page before adding to their basket, so
 * something has to carry it across that hop. This module is that carrier.
 *
 * sessionStorage rather than the URL: product cards navigate with
 * `onClick` + `navigate()` on purpose (the recommender depends on it), and a
 * queryID in a URL would be shared and indexed along with it.
 */

/** The Algolia index basket adds are credited to. */
export const PRODUCTS_INDEX = "products"

/**
 * How long a search stays responsible for a basket add. Long enough to read
 * a product page and configure options, short enough that a basket add an
 * hour later is not credited to a search the shopper has forgotten.
 */
export const ATTRIBUTION_TTL_MS = 30 * 60 * 1000

const STORAGE_KEY = "ff:algolia:query-attribution"

/**
 * Reads the stored attributions.
 *
 * Every access is guarded: sessionStorage throws outright when site data is
 * blocked, and a half-written value would otherwise take the basket down.
 *
 * @returns {Object<string, {queryID: string, at: number}>}
 */
const readStore = () => {
  if (typeof window === "undefined") {
    return {}
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null

    return parsed && typeof parsed === "object" ? parsed : {}
  } catch (err) {
    // Blocked storage or corrupted JSON: no attribution, no crash.
    return {}
  }
}

/**
 * @param {Object<string, {queryID: string, at: number}>} store
 * @returns {void}
 */
const writeStore = (store) => {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (err) {
    // Losing attribution is acceptable; throwing inside a click is not.
  }
}

/**
 * Records that `queryID` is the search which surfaced `objectID`.
 *
 * Gated on analytics consent for the same reason the userToken is: without
 * it we hold no search identifiers for the visitor at all.
 *
 * @param {string} objectID - The product's Algolia objectID (its mongo_id).
 * @param {string} queryID - The queryID Algolia returned with the search.
 * @returns {void}
 */
export const rememberSearchQueryId = (objectID, queryID) => {
  if (!objectID || !queryID || !hasAnalyticsConsent()) {
    return
  }

  const store = readStore()

  store[objectID] = { queryID, at: Date.now() }

  writeStore(store)
}

/**
 * The search responsible for `objectID`, or null if there isn't one.
 *
 * @param {string} objectID
 * @returns {string|null}
 */
export const recallSearchQueryId = (objectID) => {
  if (!objectID || !hasAnalyticsConsent()) {
    return null
  }

  const entry = readStore()[objectID]

  if (!entry?.queryID || Date.now() - entry.at >= ATTRIBUTION_TTL_MS) {
    return null
  }

  return entry.queryID
}

/**
 * Drops the attribution for `objectID`, so one search is credited with one
 * basket add and a shopper re-adding the same product does not inflate it.
 *
 * @param {string} objectID
 * @returns {void}
 */
const forgetSearchQueryId = (objectID) => {
  const store = readStore()

  if (!(objectID in store)) {
    return
  }

  delete store[objectID]
  writeStore(store)
}

/**
 * The `objectData` entry for a basket add, with a price Algolia will accept.
 *
 * Prices here are arithmetic results - unit price times quantity, summed
 * across a kit - so binary floating point routinely produces values like
 * `372.78999999999996`. Algolia rejects those with a 422: "Price must be a
 * valid decimal number and total length must be no longer than 16
 * characters". Rounding to pence both fixes the length and matches what the
 * shopper was actually charged.
 *
 * A price that is not a finite number would serialise as `null` and earn the
 * same 422, so it is dropped instead. The conversion still counts towards
 * re-ranking without a value, and a lost event would count for nothing.
 *
 * @param {number} price
 * @param {number} quantity
 * @returns {{price?: number, quantity: number}}
 */
const toObjectData = (price, quantity) => {
  if (!Number.isFinite(price)) {
    return { quantity }
  }

  return { price: Math.round(price * 100) / 100, quantity }
}

/**
 * Tells Algolia a searched-for product reached the basket.
 *
 * Silent when the add did not follow a search - an unattributed conversion
 * is no use to Dynamic Re-Ranking and would only muddy the conversion rate.
 *
 * @param {Object} params
 * @param {string} params.objectID - The product's mongo_id.
 * @param {number} params.price - Unit price added.
 * @param {number} params.quantity - Units added.
 * @returns {void}
 */
export const sendAddedToCartAfterSearch = ({ objectID, price, quantity }) => {
  const queryID = recallSearchQueryId(objectID)

  if (!queryID) {
    return
  }

  aa("addedToCartObjectIDsAfterSearch", {
    index: PRODUCTS_INDEX,
    eventName: "Product Added To Basket",
    objectIDs: [objectID],
    queryID,
    currency: "GBP",
    objectData: [toObjectData(price, quantity)],
  })

  forgetSearchQueryId(objectID)
}
